import { formatAddress } from '@/helpers/formatAddress';
import { formatPhoneNumber } from '@/helpers/formatPhoneNumber';
import {
  isPackageEditable,
  PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE,
  shouldShowPackageUnderReviewAlert,
} from '@/helpers/packageEditability';
import usePermissions from '@/hooks/usePermissions';
import { useToastSuccessSavedPackageMessage } from '@/hooks/useSavedPackageMessage';
import { useGetDocumentsQuery } from '@/services/documents';
import {
  useSaveInstallWaterHeaterPackageMutation,
  useSubmitInstallWaterHeaterPackageMutation,
} from '@/services/installWaterHeaterPackage';
import { Alert, DateInput, toast, useOpenClose } from '@palmetto/palmetto-components';
import { Field, Formik, FormikValues } from 'formik';
import { DateTime } from 'luxon';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import * as yup from 'yup';

import {
  Applicant,
  ApplicantType,
  DeepPartial,
  InstallWaterHeaterPackage as IInstallWaterHeaterPackage,
  InstallationPackagePhotoCategory,
  MilestoneType,
  QueueStatus,
  QueueType,
  RequirementFlagSettingsType,
  UserPermissions,
  WaterHeaterEquipmentItem,
  WaterHeaterInstallPackageFileCategory,
  WaterHeaterSystem,
} from 'types';

import { convertToISODateTimeString, DATE_FORMAT } from '../../helpers/dates';
import { useGetAccountQuery } from '../../services/accounts';
import { isErrorWithData } from '../../services/helpers';
import { useGetQueueByAccountIdAndTypeQuery } from '../../services/queues';
import { ReviewPackageActions } from '../../types/ReviewPackage';
import { ActionsList } from '../ActionsList';
import { useAuth } from '../auth/authProvider';
import { isInternalUser } from '../auth/RequireIsInternalUser';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import { ContractDocumentSection } from '../MilestonePackagePartials/ContractDocumentSection';
import { Footer } from '../MilestonePackagePartials/Footer';
import { HVACInstallContractInfo } from '../MilestonePackagePartials/hvac/HVACInstallContractInfo';
import WarrantyRegistration from '../MilestonePackagePartials/hvac/WarrantyRegistration';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { AttestationSection } from '../MilestonePackagePartials/waterHeater/AttestationSection';
import { InstallContract } from '../MilestonePackagePartials/waterHeater/InstallContract';
import { InstallPhotos } from '../MilestonePackagePartials/waterHeater/InstallPhotos';
import { SystemPhotos } from '../MilestonePackagePartials/waterHeater/SystemPhotos';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';
import { HVACAttestationModal } from '../ProductionValueCheck/HvacAttestationModal';
import { FlagBox } from '../QueueFlags/FlagBox';
import { QueueFlagsContext, QueueFlagsProvider } from '../QueueFlags/QueueFlagsContext';
import { SkeletonBox } from '../SkeletonBox';
import { AhriCertificateSection } from '../SystemDesign/waterHeater/AhriCertificateSection';
import { SystemSection } from '../SystemDesign/waterHeater/SystemSection';

interface InstallPackageProps {
  canSubmitInstallPackage: boolean;
}

const InstallWaterHeaterPackageSchema = yup.object().shape({
  systemInstallDate: yup.string().required('System Install Date is a required field.'),
  projectAttestationCheckbox: yup.boolean().oneOf([true], 'Attestation is a required field.'),
  systems: yup.array().of(
    yup.object().shape({
      equipment: yup.object().shape({
        items: yup.array().of(
          yup.object().shape({
            manufacturer: yup.string().required('Manufacturer is required.'),
            model: yup.string().required('Make/Model is required.'),
            serialNumbers: yup
              .array()
              .of(yup.string().required('Serial Number is required.'))
              .min(1, 'Serial Number is required.'),
          }),
        ),
      }),
    }),
  ),
});

const projectAttestLabel =
  'By checking this box, I confirm that the following have been completed for this system installation: Permitting, Leak Testing, Code Compliance, Warranty Eligibility, and Manufacturer Registration';

export const formatInstallWaterHeaterPackagesData = (
  values: FormikValues,
): DeepPartial<IInstallWaterHeaterPackage> => {
  return {
    systemDesign: {
      isPreliminary: false,
      systems: values?.systems?.map((system: WaterHeaterSystem) => {
        return {
          name: system.name,
          ahriNumber: system.ahriNumber,
          equipment: {
            totalCost: system.equipment.totalCost,
            items: system.equipment.items.map((item) => ({
              type: item.type,
              manufacturer: item.manufacturer || 'unknown',
              model: item.model || 'unknown',
              quantity: item.quantity,
              serialNumbers: item.serialNumbers,
              fuelType: item.fuelType,
              waterHeaterType: item.waterHeaterType,
              tankCapacity: item.tankCapacity,
              tanklessFlowRate: item.tanklessFlowRate,
            })),
          },
          sitePrep: system?.sitePrep
            ? {
                totalCost: system.sitePrep.totalCost,
                items: (system.sitePrep?.items || []).map((item) => ({
                  name: item.name,
                })),
              }
            : undefined,
          discounts: system?.discounts
            ? {
                totalCost: system.discounts.totalCost,
                items: (system.discounts?.items || []).map((item) => ({
                  name: item.name,
                })),
              }
            : undefined,
        };
      }),
    },
    systemInstallDate: values.systemInstallDate
      ? { palmetto: convertToISODateTimeString(values.systemInstallDate) }
      : undefined,
    projectAttestation: values.projectAttestationCheckbox
      ? { attestmentText: values.projectAttestationCheckbox ? projectAttestLabel : '' }
      : undefined,
  };
};

const flattenSystemItems = (items: WaterHeaterEquipmentItem[]) => {
  const newItems: any[] = [];

  for (const item of items) {
    if (item.quantity > 1) {
      for (let i = 0; i < item.quantity; i++) {
        newItems.push({
          ...item,
          quantity: 1,
        });
      }
    } else {
      newItems.push({ ...item });
    }
  }
  return newItems;
};

const documentsPreviouslyUploadedToWaterHeaterInstallation = [
  InstallationPackagePhotoCategory.equipmentDataPlate,
  InstallationPackagePhotoCategory.wideInstallationView,
  InstallationPackagePhotoCategory.tpReliefValve,
  InstallationPackagePhotoCategory.drainPanDrainLine,
];

export const InstallWaterHeaterPackage = ({ canSubmitInstallPackage }: InstallPackageProps) => {
  const navigate = useNavigate();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const permissions = usePermissions();
  const isLightReachQCTech = permissions.includes(UserPermissions.lightReachQCTech);
  const userOnlyHasReadPermissions = permissions?.every((permission) => permission.includes(UserPermissions.reader));
  const { id = '', action } = useParams<{ id: string; action: ReviewPackageActions }>();
  const { data: queue, isLoading: isQueueLoading } = useGetQueueByAccountIdAndTypeQuery(
    {
      accountId: id,
      type: QueueType.installPackage,
    },
    { skip: !id },
  );
  const { getFlagsByFlagType } = useContext(QueueFlagsContext);
  const checkboxCategories = useMemo(() => Object.values(WaterHeaterInstallPackageFileCategory), []);
  const showCheckboxes = isInternalUser(claims, true);

  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id, { skip: !id });
  const primaryApplicant = account?.applicants.find(
    (applicant: Applicant) => applicant.type === ApplicantType.primary,
  );
  const systemInstallDate = (values: any): Date | null => {
    return values.systemInstallDate ? DateTime.fromFormat(values.systemInstallDate, DATE_FORMAT).toJSDate() : null;
  };

  const handleDateChange = useCallback((date: Date | [Date, Date] | null, setFieldValue: any) => {
    if (!date || !(date instanceof Date)) return;
    setFieldValue('systemInstallDate', DateTime.fromJSDate(date).toUTC().toFormat(DATE_FORMAT));
  }, []);

  const [warrantiesConfirmed, setWarrantiesConfirmed] = useState(action !== ReviewPackageActions.review);

  const minDate = DateTime.fromISO('2022-01-01').toJSDate();
  const maxDate = DateTime.local().endOf('day').toJSDate();
  const [triggerSubmit] = useSubmitInstallWaterHeaterPackageMutation();
  const [triggerSave] = useSaveInstallWaterHeaterPackageMutation();

  const {
    isOpen: isAttestationOpen,
    handleOpen: setAttestationSubmissionOpen,
    handleClose: setAttestationSubmissionClose,
  } = useOpenClose();
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [userAttestation, setUserAttestation] = useState(false);
  const isLoading = isAccountLoading || isQueueLoading;
  const { data: wasADocumentPreviouslyUploadedToWaterHeaterInstallation } = useGetDocumentsQuery(
    {
      accountId: id,
      documentTypeFilter: documentsPreviouslyUploadedToWaterHeaterInstallation,
    },
    { skip: !id },
  );

  const { data: installationDocuments } = useGetDocumentsQuery(
    {
      accountId: id,
      documentTypeFilter: [InstallationPackagePhotoCategory.waterHeaterInstallationPhotos],
    },
    { skip: !id },
  );

  const customerContractFlags = getFlagsByFlagType(RequirementFlagSettingsType.customerContract);
  const waterHeaterInstallationPhotosFlag = getFlagsByFlagType(
    RequirementFlagSettingsType.waterHeaterInstallationPhotos,
  );
  const showInstallPhotosForInitialSubmission =
    !wasADocumentPreviouslyUploadedToWaterHeaterInstallation?.length &&
    !isFinCoUser &&
    (!queue || (queue && queue.status === QueueStatus.submitted));
  const showInstallPhotosForEPCUser =
    !isFinCoUser && (waterHeaterInstallationPhotosFlag?.length || (queue && queue.status === QueueStatus.submitted));

  const canEditPackage = isPackageEditable({
    queue,
    isFinCoUser,
  });
  const showUnderReviewAlert = shouldShowPackageUnderReviewAlert({
    queue,
    isFinCoUser,
  });
  const triggerSuccessMessage = useToastSuccessSavedPackageMessage(id, MilestoneType.install);

  const VALIDATION_ERROR = 'ValidationError';

  useEffect(() => {
    if (!action || !ReviewPackageActions[action]) {
      navigate(`/accounts/${id}`, { replace: true });
    }
    if (action === ReviewPackageActions.review && (!isFinCoUser || !isLightReachQCTech)) {
      if (queue) {
        navigate(`/accounts/${id}/installation-package/edit`, { replace: true });
      } else {
        navigate(`/accounts/${id}/installation-package/create`, { replace: true });
      }
    }
  }, [id, navigate, action, isFinCoUser, isLightReachQCTech, queue]);

  const accountInitialValues = useMemo(() => {
    if (!account) {
      return {};
    }
    const systems = account?.systemDesign?.systems.reduce((acc: WaterHeaterSystem[], system: WaterHeaterSystem) => {
      const equipment = flattenSystemItems(system.equipment.items).map((item) => {
        const serialNumbers =
          item.serialNumbers && item.serialNumbers.length ? item.serialNumbers : Array(item.quantity).fill('');

        return Object.assign(item, { isSerialNumberRequired: true, serialNumbers });
      });

      const totalCost = system.equipment.totalCost || 0;

      const updatedSystem = {
        ...system,
        equipment: {
          ...system.equipment,
          totalCost,
          items: equipment,
        },
      } as WaterHeaterSystem;
      acc.push(updatedSystem);
      return acc;
    }, [] as WaterHeaterSystem[]);
    return {
      systemInstallDate: account?.systemInstallDate?.palmetto
        ? DateTime.fromISO(account.systemInstallDate.palmetto).toUTC().toFormat(DATE_FORMAT)
        : '',
      systems,
    };
  }, [account]);

  const canSubmitReview = action === ReviewPackageActions.review && queue;
  const initialValues = {
    ...accountInitialValues,
  };

  const validateInstallationDocuments = (): void => {
    if (!installationDocuments?.length && !queue) {
      const validationError = new Error('At least one installation document is required.');
      validationError.name = VALIDATION_ERROR;
      throw validationError;
    }
  };

  const handleSubmit = async (values: FormikValues, { setSubmitting }: any) => {
    const formValues = formatInstallWaterHeaterPackagesData(values);

    if (!userAttestation) {
      setAttestationSubmissionOpen();
      return;
    }

    try {
      validateInstallationDocuments();
    } catch (e) {
      if (e instanceof Error) {
        toast.error(e.message);
      }
      return;
    }

    try {
      setSubmitting(true);
      await triggerSubmit({ accountId: id, installPackage: formValues as IInstallWaterHeaterPackage }).unwrap();
      toast.success('Install Package Submitted');
      navigate(`/accounts/${id}`);
    } catch (e: any) {
      toast.error(e?.data?.message || 'Error submitting install package');
    } finally {
      setSubmitting(false);
      setUserAttestation(false);
      setAttestationSubmissionClose();
    }
  };

  const handleSave = async ({
    accountId,
    data,
    targetStatus,
  }: {
    accountId: string;
    data: FormikValues;
    targetStatus?: QueueStatus;
  }) => {
    try {
      if (targetStatus) {
        validateInstallationDocuments();
      }

      const formValues = formatInstallWaterHeaterPackagesData(data);
      await triggerSave({ accountId, savePackage: formValues }).unwrap();
      triggerSuccessMessage();
    } catch (e) {
      console.error(e);
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        throw new Error(errorMessage, { cause: e });
      } else if (e instanceof Error && e.name === VALIDATION_ERROR) {
        throw e;
      } else {
        console.error(e);
        throw new Error('Error saving Install Package', { cause: e });
      }
    }
  };

  const queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser: boolean =
    !isFinCoUser &&
    queue &&
    (queue.status === QueueStatus.conditionallyApproved || queue.status === QueueStatus.approved);

  if (isLoading) {
    return (
      <MainContainer>
        <FormSection title="Loading...">
          <SkeletonBox height="25px" width="100" />
        </FormSection>
      </MainContainer>
    );
  }

  const attestationModalText =
    'By submitting this package, I attest that all equipment listed is accurate to the equipment installed for the customer, and that all equipment is working to manufacturer specifications.';

  return (
    <>
      <QueueFlagsProvider queueId={queue?.id}>
        <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.installPackage} />
        <MainContainer>
          <Formik
            initialValues={initialValues}
            validationSchema={InstallWaterHeaterPackageSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ setFieldValue, values, setSubmitting, isSubmitting, touched, errors }) => (
              <>
                <EnhancedFormikForm>
                  <ActionsList
                    milestoneType={MilestoneType.install}
                    accountId={queue?.account?.id}
                    canSubmitPackage={canSubmitInstallPackage}
                    isLoading={isLoading}
                    isFirstSubmission={!queue}
                    canSubmitReview={canSubmitReview}
                    disableApproval={!warrantiesConfirmed}
                    onSave={handleSave}
                    disabled={queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser || !canEditPackage}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    queue={queue}
                  />

                  {queue && (queue.status === QueueStatus.rejected || isFinCoUser) ? (
                    <QueueBanner
                      setIsNotesOpen={setIsNotesOpen}
                      queueType={QueueType.installPackage}
                      isFinCoUser={isFinCoUser}
                    />
                  ) : null}
                  {showUnderReviewAlert ? (
                    <Alert
                      variant="warning"
                      title="Install Package In Review Queue"
                      message={PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE}
                      hasIcon
                    />
                  ) : null}
                  {action === ReviewPackageActions.review && !canSubmitReview ? (
                    <Alert
                      variant="warning"
                      title="Install Package Not Submitted"
                      message="This install package is not ready to be reviewed yet. It has not been submitted."
                      hasIcon
                    />
                  ) : null}
                  {(isFinCoUser || (customerContractFlags?.length || 0) > 0) && (
                    <ContractDocumentSection showSeparatorBorder={true} />
                  )}
                  <FormSection title="System Install Date">
                    <FlagBox
                      dataKey="System Install Date"
                      baseCategory="systemInstallDate"
                      flagType={RequirementFlagSettingsType.systemInstallDateWaterHeater}
                      displayWithNoQueue
                    >
                      <Field
                        label=""
                        name="systemInstallDate"
                        id="systemInstallDate"
                        dateFormat={DATE_FORMAT}
                        width="2xl"
                        value={values.systemInstallDate}
                        component={DateInput}
                        datePickerProps={{
                          maxDate,
                          minDate,
                          onChange: (date: Date | [Date, Date] | null) => handleDateChange(date, setFieldValue),
                          openToDate: systemInstallDate(values) ?? undefined,
                        }}
                        textInputProps={{
                          onClear: () => setFieldValue('systemInstallDate', ''),
                        }}
                        isDisabled={isSubmitting || !canEditPackage}
                        error={touched.systemInstallDate && (errors.systemInstallDate as string)}
                      />
                    </FlagBox>
                  </FormSection>
                  {isFinCoUser && (
                    <FormSection title="Customer Information">
                      <table>
                        <tbody>
                          <tr>
                            <td>Partner Licensed Org:</td>
                            <td>
                              <Link to={`/settings/organizations/${account?.licensedOrganizationId}`}>
                                {account?.licensedOrganizationId?.toUpperCase() ?? ''}
                              </Link>
                            </td>
                          </tr>
                          <tr>
                            <td>Partner Org:</td>
                            <td>
                              <Link to={`/settings/organizations/${account?.organizationId}`}>
                                {account?.organizationId?.toUpperCase() ?? ''}
                              </Link>
                            </td>
                          </tr>
                          <tr>
                            <td>Primary Contact Details:</td>
                            <td>
                              {primaryApplicant?.firstName} {primaryApplicant?.lastName}
                              {' - '}
                              {primaryApplicant?.email}
                              {' - '}
                              {primaryApplicant?.phoneNumber ? formatPhoneNumber(primaryApplicant.phoneNumber) : ''}
                            </td>
                          </tr>
                          <tr>
                            <td>Property Address:</td>
                            <td>{formatAddress(account?.address)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </FormSection>
                  )}
                  {showInstallPhotosForInitialSubmission ? (
                    <InstallPhotos
                      showCheckboxes={showCheckboxes}
                      checkboxCategories={checkboxCategories}
                      isPackageEditable={canEditPackage}
                    />
                  ) : (
                    <>
                      {(isFinCoUser || showInstallPhotosForEPCUser) && (
                        <InstallPhotos
                          showCheckboxes={showCheckboxes}
                          checkboxCategories={checkboxCategories}
                          isPackageEditable={canEditPackage}
                        />
                      )}
                      <InstallContract
                        showCheckboxes={showCheckboxes}
                        checkboxCategories={checkboxCategories}
                        isPackageEditable={canEditPackage}
                      />
                      {action === ReviewPackageActions.review && (
                        <FormSection>
                          <HVACInstallContractInfo
                            isSubmitting={isSubmitting}
                            isLoading={isLoading}
                            userOnlyHasReadPermissions={userOnlyHasReadPermissions}
                            isPackageEditable={canEditPackage}
                          />
                        </FormSection>
                      )}
                      <SystemPhotos
                        showCheckboxes={showCheckboxes}
                        checkboxCategories={checkboxCategories}
                        isPackageEditable={canEditPackage}
                      />
                    </>
                  )}
                  <SystemSection
                    isInstallPackage
                    isPreliminary={account?.systemDesign?.isPreliminary}
                    isPackageEditable={canEditPackage}
                  />
                  {isFinCoUser && action === ReviewPackageActions.review && (
                    <>
                      <AhriCertificateSection
                        showCheckboxes={showCheckboxes}
                        checkboxCategories={checkboxCategories}
                        isPackageEditable={canEditPackage}
                      />
                      <WarrantyRegistration
                        onValidityChange={setWarrantiesConfirmed}
                        isPackageEditable={canEditPackage}
                      />
                    </>
                  )}
                  {action !== ReviewPackageActions.review && (
                    <AttestationSection
                      projectAttestLabel={projectAttestLabel}
                      setFieldValue={setFieldValue}
                      values={values}
                      isSubmitting={isSubmitting}
                      touched={touched}
                      errors={errors}
                      isPackageEditable={canEditPackage}
                    />
                  )}
                  <Footer
                    canSubmitPackage={canSubmitInstallPackage}
                    isLoading={isLoading}
                    isFirstSubmission={!queue}
                    canSubmitReview={canSubmitReview}
                    disableApproval={!warrantiesConfirmed}
                    onSave={handleSave}
                    disabled={queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser || !canEditPackage}
                  />
                </EnhancedFormikForm>
                <HVACAttestationModal
                  title="Install Submission Confirmation"
                  label={attestationModalText}
                  values={values}
                  handleSubmit={handleSubmit}
                  isOpen={isAttestationOpen}
                  onDismiss={setAttestationSubmissionClose}
                  onAttestationConfirm={setUserAttestation}
                  isLoading={isSubmitting}
                  setSubmitting={setSubmitting}
                />
              </>
            )}
          </Formik>
        </MainContainer>
      </QueueFlagsProvider>
    </>
  );
};
